import SimpleUri from '@ms/odsp-utilities/lib/uri/SimpleUri';
import { equalsCaseInsensitive as equals } from '@ms/odsp-utilities/lib/string/StringHelper';

export interface IItemUrlContext {
  webAbsoluteUrl?: string;
  listUrl?: string;
}

export interface IGetUrlPartsOptions {
  /**
   * Fully qualified (or server-relative) URL to the item.
   *
   * @type {string}
   *
   * @example
   *  https://contoso.sharepoint.com/teams/finance/Shared_Documents/reports/Yearly.xlsx
   * @example
   *  /teams/finance/Shared%20Documents/reports/Yearly.xlsx
   */
  path?: string;
  /**
   * Fully-qualified (or server-relative) URL to the list root.
   *
   * @type {string}
   * @example
   *  https://contoso-my.sharepoint.com/personal/me_contoso_com/Documents
   * @example
   *  /personal/me_contoso_com/Documents
   * @example
   *  /teams/finance/Shared%20Documents
   */
  listUrl?: string;
  /**
   * Fully-qualified (or server-relative) URL to the site root.
   *
   * @type {string}
   *
   * @example
   *  https://contoso-my.sharepoint.com/personal/me_contoso_com
   * @example
   *  https://contoso.sharepoint.com/teams/finance
   * @example
   *  /teams/finance
   * @example
   *  /personal/me_contoso_com
   */
  webUrl?: string;
  /**
   * Whether or not the list URL may be inferred
   */
  mayInferListUrl?: boolean;
}

/**
 * Represents the available URL parts extractable for an item.
 *
 * @export
 * @interface IItemUrlPartsBasic
 */
export interface IItemUrlPartsBasic {
  /**
   * Gets the fully-qualified path to the item.
   *
   * @type {string}
   */
  fullItemUrl: string | undefined;
  /**
   * Gets the server-relative path to the item.
   * If no path was provided, this will be undefined.
   *
   * Will be '/' for the server-relative root.
   * Otherwise, presence of a trailing '/' depends on the input path.
   *
   * @type {string}
   */
  serverRelativeItemUrl: string | undefined;
  /**
   * Gets the fully-qualified path to the list containing the item.
   *
   * @type {string}
   */
  fullListUrl: string | undefined;
  /**
   * Gets the normalized list URL for the item.
   * If the list is on the current server domain, this will be server-relative.
   * If the list is the current default list or no list is available, this will be undefined.
   *
   * Will be '/' for the server-relative root.
   * Otherwise, presence of a trailing '/' depends on the input path.
   *
   * @type {string}
   */
  normalizedListUrl: string | undefined;

  /**
   * Gets the server-relative path to the list.
   *
   * Will be '/' for the server-relative root.
   * Otherwise, presence of a trailing '/' depends on the input path.
   *
   * @type {string}
   */
  serverRelativeListUrl: string | undefined;

  /**
   * Gets the fully-qualified path to the item if on an alternate domain.
   * Gets the server-relative path to the item if on the same domain.
   *
   * Will be '/' for the server-relative root.
   * Otherwise, presence of a trailing '/' depends on the input path.
   *
   * @type {string}
   */
  normalizedItemUrl: string | undefined;

  /**
   * Gets the path to the item relative to the list.
   */
  listRelativeItemUrl: string | undefined;

  /**
   * Gets the path to the item relative to is web.
   *
   * @type {string}
   * @memberOf IItemUrlParts
   */
  webRelativeItemUrl: string | undefined;
}

export interface IItemUrlPartsParams {
  defaultFullWebUrl?: string;
  defaultListUrl?: string;
  options?: IGetUrlPartsOptions;
}

/**
 * Backing implementation of the result from a call to ItemUrlHelper.getUrlParts.
 * This class relies heavily on lazy-initialization of values in order to improve performance.
 *
 * @class ItemUrlParts
 * @implements {IItemUrlParts}
 */
export class ItemUrlPartsBasic implements IItemUrlPartsBasic {
  public get serverRelativeItemUrl(): string | undefined {
    return convertToRootUrl(this._getServerRelativeItemUrl());
  }

  public get serverRelativeListUrl(): string | undefined {
    return convertToRootUrl(this._getServerRelativeListUrl());
  }

  public get fullListUrl(): string | undefined {
    return this._getFullListUrl();
  }

  public get normalizedListUrl(): string | undefined {
    return convertToRootUrl(this._getNormalizedListUrl());
  }

  public get fullItemUrl(): string | undefined {
    return this._getFullItemUrl();
  }

  public get normalizedItemUrl(): string | undefined {
    return convertToRootUrl(this._getNormalizedItemUrl());
  }

  public get listRelativeItemUrl(): string | undefined {
    return this._getListRelativeItemUrl();
  }

  public get webRelativeItemUrl(): string | undefined {
    return this._getWebRelativeItemUrl();
  }

  protected path: string | undefined;
  protected listUrl: string | undefined;
  protected webUrl: string | undefined;
  protected mayInferListUrl: boolean;

  protected defaultFullWebUrl: string | undefined;
  protected defaultListUrl: string | undefined;

  constructor(params: IItemUrlPartsParams) {
    /* tslint:disable:no-use-before-declare */
    const {
      defaultFullWebUrl,
      defaultListUrl,
      options: {
        path = undefined,
        listUrl = undefined,
        webUrl = undefined,
        mayInferListUrl = false
      } = {}
    } = params;
    /* tslint:enable:no-use-before-declare */

    this.defaultFullWebUrl = convertFromRootUrl(defaultFullWebUrl)!;
    this.defaultListUrl = convertFromRootUrl(defaultListUrl)!;

    this.path = convertFromRootUrl(path);
    this.listUrl = convertFromRootUrl(listUrl);
    this.webUrl = convertFromRootUrl(webUrl);
    this.mayInferListUrl = mayInferListUrl;
  }

  protected _getCurrentAuthority(): string {
    const currentAuthority = new SimpleUri(this.defaultFullWebUrl).authority;

    this._getCurrentAuthority = () => currentAuthority;

    return currentAuthority;
  }

  protected _getNormalizedItemUrl(): string | undefined {
    let normalizedItemUrl: string | undefined;

    const fullItemUrl = this._getFullItemUrl();

    if (fullItemUrl !== void 0) {
      const fullItemUri = new SimpleUri(fullItemUrl);

      if (equals(fullItemUri.authority, this._getCurrentAuthority())) {
        normalizedItemUrl = fullItemUri.path;
      } else {
        normalizedItemUrl = fullItemUrl;
      }
    }

    this._getNormalizedItemUrl = () => normalizedItemUrl;

    return normalizedItemUrl;
  }

  protected _getNormalizedListUrl(): string | undefined {
    let normalizedListUrl: string | undefined;

    const fullListUrl = this._getFullListUrl();

    const defaultListUrl = this._getDefaultListUrl();

    if (fullListUrl !== void 0 && !equals(fullListUrl, defaultListUrl)) {
      if (equals(new SimpleUri(fullListUrl).authority, this._getCurrentAuthority())) {
        // Remove the domain if the list is on the current domain.
        normalizedListUrl = this._getServerRelativeListUrl();
      } else {
        // The list on not on the current server and is not the default.
        normalizedListUrl = fullListUrl;
      }
    }

    this._getNormalizedListUrl = () => normalizedListUrl;

    return normalizedListUrl;
  }

  protected _getServerUrl(): string {
    // One of the provided inputs should have a domain.
    // Extract the authority from that input to use as the base for all full URLs.

    const serverUrl =
      this.path && new SimpleUri(this.path).authority ||
      this.listUrl && new SimpleUri(this.listUrl).authority ||
      this.webUrl && new SimpleUri(this.webUrl).authority ||
      this.defaultListUrl && new SimpleUri(this.defaultListUrl).authority ||
      new SimpleUri(this.defaultFullWebUrl).authority;

    this._getServerUrl = () => serverUrl;

    return serverUrl;
  }

  protected _getServerRelativeListUrl(): string | undefined {
    let serverRelativeListUrl: string | undefined;

    if (this.listUrl !== void 0) {
      serverRelativeListUrl = new SimpleUri(this.listUrl).path;
    } else {
      // Since no list URL was provided, we will check if we can use the default list URL
      const defaultListUrl = this._getDefaultListUrl();

      const mayInferListUrl = this.mayInferListUrl;

      if (defaultListUrl !== void 0 || mayInferListUrl) {
        const serverRelativePath = this._getServerRelativePath();
        const serverRelativeDefaultListUrl = new SimpleUri(defaultListUrl).path;

        if (serverRelativePath === void 0 && (
          this.webUrl === void 0 ||
          serverRelativeDefaultListUrl && startsWith(appendDelimiter(serverRelativeDefaultListUrl),
            appendDelimiter(this._getServerRelativeWebUrl()))) ||
          serverRelativeDefaultListUrl && startsWith(appendDelimiter(serverRelativePath),
            appendDelimiter(serverRelativeDefaultListUrl))) {
          // If the default list URL contains the specified path (or there is no path), then the default list is the correct list.
          serverRelativeListUrl = serverRelativeDefaultListUrl;
        } else if (serverRelativePath !== void 0 && (this.defaultListUrl === '' || mayInferListUrl)) {
          // If defaultListUrl was actually the empty string, or inference is permitted,
          // attempt to reconstruct the list URL as the next segment after the web URL within the item's path.
          const serverRelativeWebUrl = this._getServerRelativeWebUrl();

          if (startsWith(appendDelimiter(serverRelativePath), appendDelimiter(serverRelativeWebUrl))) {
            // Assume that the list name is the next segment of the path after the webUrl.
            // NOTE: This is a bug since a list can exist in folders on the web!
            const listName = new SimpleUri(serverRelativePath).segments[new SimpleUri(serverRelativeWebUrl).segments.length];

            serverRelativeListUrl = `${appendDelimiter(serverRelativeWebUrl)}${listName}`;
          }
        }
      }
    }

    this._getServerRelativeListUrl = () => serverRelativeListUrl;

    return serverRelativeListUrl;
  }

  protected _getFullListUrl(): string | undefined {
    let fullListUrl: string | undefined;

    const serverUrl = this._getServerUrl();
    const serverRelativeListUrl = this._getServerRelativeListUrl();

    if (serverRelativeListUrl !== void 0) {
      fullListUrl = `${serverUrl}${serverRelativeListUrl}`;
    }

    return fullListUrl;
  }

  protected _getFullItemUrl(): string | undefined {
    let fullItemUrl: string | undefined;

    const serverUrl = this._getServerUrl();

    const serverRelativeItemUrl = this._getServerRelativeItemUrl();

    if (serverRelativeItemUrl !== void 0) {
      fullItemUrl = `${serverUrl}${serverRelativeItemUrl}`;
    }

    this._getFullItemUrl = () => fullItemUrl;

    return fullItemUrl;
  }

  protected _getServerRelativeWebUrl(): string | undefined {
    let serverRelativeWebUrl: string | undefined;

    let serverRelativeListUrl: string | undefined;

    if (this.webUrl !== void 0) {
      serverRelativeWebUrl = new SimpleUri(this.webUrl).path;
    } else if ((this.defaultListUrl || this.listUrl) && !this.mayInferListUrl &&
      (serverRelativeListUrl = this._getServerRelativeListUrl()) !== void 0) {
      // If there is list information, and the listUrl may not be inferred from this web URL (which would be recursive),
      // attempt to infer the web from the list.
      const serverRelativeListUri = new SimpleUri(serverRelativeListUrl);

      serverRelativeWebUrl = `${serverRelativeListUri.segments.slice(0, -1).join('/')}`;
    } else if (this.defaultFullWebUrl) {
      serverRelativeWebUrl = new SimpleUri(this.defaultFullWebUrl).path;
    }

    this._getServerRelativeWebUrl = () => serverRelativeWebUrl;

    return serverRelativeWebUrl;
  }

  protected _getServerRelativePath(): string | undefined {
    let serverRelativePath: string | undefined;

    if (this.path !== void 0) {
      // Path is the only pointer directly to the item.
      serverRelativePath = new SimpleUri(this.path).path;
    }

    this._getServerRelativePath = () => serverRelativePath;

    return serverRelativePath;
  }

  protected _getServerRelativeItemUrl(): string | undefined {
    let serverRelativeItemUrl: string | undefined;

    if (this.path) {
      serverRelativeItemUrl = this._getServerRelativePath();
    } else if (this.listUrl) {
      serverRelativeItemUrl = this._getServerRelativeListUrl();
    } else if (this.webUrl) {
      serverRelativeItemUrl = this._getServerRelativeWebUrl();
    } else if (this.defaultListUrl) {
      serverRelativeItemUrl = this._getServerRelativeListUrl();
    } else if (this.defaultFullWebUrl) {
      serverRelativeItemUrl = this._getServerRelativeWebUrl();
    }

    this._getServerRelativeItemUrl = () => serverRelativeItemUrl;

    return serverRelativeItemUrl;
  }

  protected _getListRelativeItemUrl(): string | undefined {
    let listRelativeItemUrl: string | undefined;

    const serverRelativePath = this._getServerRelativePath();
    const serverRelativeListUrl = this._getServerRelativeListUrl();

    let serverRelativeListUrlStem: string;

    if (this.path !== void 0 &&
      serverRelativeListUrl !== void 0 &&
      startsWith(appendDelimiter(serverRelativePath), serverRelativeListUrlStem = appendDelimiter(serverRelativeListUrl))) {
      listRelativeItemUrl = serverRelativePath && serverRelativePath.substring(serverRelativeListUrlStem.length);
    }

    this._getListRelativeItemUrl = () => listRelativeItemUrl;

    return listRelativeItemUrl;
  }

  protected _getWebRelativeItemUrl(): string | undefined {
    let webRelativeItemUrl: string | undefined;

    const serverRelativePath = this._getServerRelativePath();
    const serverRelativeWebUrl = this._getServerRelativeWebUrl();

    let serverRelativeWebUrlStem: string;

    if (this.path !== void 0 &&
      serverRelativeWebUrl !== void 0 &&
      startsWith(appendDelimiter(serverRelativePath), serverRelativeWebUrlStem = appendDelimiter(serverRelativeWebUrl))) {
      webRelativeItemUrl = serverRelativePath && serverRelativePath.substring(serverRelativeWebUrlStem.length);
    }

    this._getWebRelativeItemUrl = () => webRelativeItemUrl;

    return webRelativeItemUrl;
  }

  protected _getDefaultListUrl(): string | undefined {
    let defaultListUrl: string | undefined;

    if (this.defaultListUrl !== void 0) {
      if (new SimpleUri(this.defaultListUrl).authority) {
        // If the default list supplies its own domain, use it instead of the current server domain.
        defaultListUrl = this.defaultListUrl;
      } else {
        // Assume the list is on the current domain.
        defaultListUrl = `${this._getCurrentAuthority()}${this.defaultListUrl}`;
      }
    }

    this._getDefaultListUrl = () => defaultListUrl;

    return defaultListUrl;
  }
}

function convertToRootUrl(url: string | undefined): string | undefined {
  if (url === '') {
    return '/';
  }

  return url;
}

function convertFromRootUrl(url: string | undefined): string | undefined {
  if (url === '/') {
    return '/';
  }

  return url;
}

export function removeDelimiter(segment: string = ''): string {
  if (segment[segment.length - 1] === '/') {
    return segment.substring(0, segment.length - 1);
  } else {
    return segment;
  }
}

export function appendDelimiter(segment: string = ''): string {
  if (segment[segment.length - 1] === '/') {
    return segment;
  } else {
    return `${segment}/`;
  }
}

export function startsWith(body: string, query: string | undefined): boolean {
  const trimmedNormalizedBody = body.toUpperCase();
  const trimmedNormalizedQuery = query && query.toUpperCase();

  return trimmedNormalizedBody.substring(0, trimmedNormalizedQuery && trimmedNormalizedQuery.length || 0) === trimmedNormalizedQuery;
}

export interface IItemUrlHelperBasicParams {
  context: IItemUrlContext;
  itemUrlPartsType?: new (params: IItemUrlPartsParams) => IItemUrlPartsBasic;
}

/**
 * Component which consumes known information about SharePoint item URLs and constructs
 * normalized URLs for use in item keys and API calls.
 *
 * Different SharePoint API methods return URLs to items, lists, and sites with different formats.
 * This component can be used to extract the necessary information from these URLs and construct
 * invariant versions.
 *
 * The logic in this component codifies all the assumptions made about the format of SharePoint URLs
 * by the web layer.
 *
 * Note, URLs passed in as '/' are assumed to mean the server-relative root. These are internally treated
 * as '' but any URL which evaluates back to the server-relative root will be returned as '/'.
 *
 * @export
 * @class ItemUrlHelperBasic
 */
export class ItemUrlHelperBasic {
  protected itemUrlContext: IItemUrlContext;

  protected itemUrlPartsType: new (params: IItemUrlPartsParams) => IItemUrlPartsBasic;

  constructor(params: IItemUrlHelperBasicParams) {
    const {
      context,
      itemUrlPartsType = ItemUrlPartsBasic
    } = params;

    this.itemUrlPartsType = itemUrlPartsType;
    this.itemUrlContext = context;
  }

  /**
   * Extracts the available URL parts for an item given available information.
   * Any or all of the inputs may be provided, though the outputs may be undefined
   * if insufficient information is provided.
   *
   * @param {IGetUrlPartsOptions} [params={}]
   * @returns {IItemUrlParts}
   */
  public getUrlParts(options?: IGetUrlPartsOptions): IItemUrlPartsBasic {
    const {
      webAbsoluteUrl,
      listUrl
    } = this.itemUrlContext;
    return new this.itemUrlPartsType({
      defaultFullWebUrl: webAbsoluteUrl,
      defaultListUrl: listUrl,
      options: options
    });
  }
}
